/**
 * The asset list (F5).
 *
 * A site register read for one purpose: *which of these could host a pilot?*
 * So the pilot-ready verdict is a column rather than a detail-page fact, and
 * capacity travels as the power/energy pair PRD §9.5 requires.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.4, PRD §5.6, §9.5
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { AssetSummary } from '@nexus/contracts';

import { AssetStatusBadge } from '../../components/domain/AssetStatusBadge';
import { Capacity } from '../../components/domain/Capacity';
import { Badge } from '../../components/ui/Badge';
import type { Column } from '../../components/ui/data-table-types';
import { DataTable } from '../../components/ui/DataTable';
import { ExportButton } from '../../components/ui/ExportButton';
import { PageHead } from '../../components/ui/PageHead';
import { Pagination } from '../../components/ui/Pagination';
import { formatNumber } from '../../lib/format';
import { assetPath } from '../../lib/routes';
import { useTableState } from '../../lib/use-table-state';

import { useAssets } from './api/assets.queries';
import { AssetFilters } from './components/AssetFilters';

/** Sort fields the API allow-lists (see `ASSET_SORT_FIELDS` on the server). */
const SORT_FIELDS = [
  'name',
  'type',
  'status',
  'ownership',
  'country',
  'capacityMw',
  'commissioningDate',
  'createdAt',
  'updatedAt',
];

export function AssetsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, toggleSort, setPage, setPageSize, setFilter, clearFilters } =
    useTableState(SORT_FIELDS);

  const query = useMemo(
    () => ({
      page: state.page,
      pageSize: state.pageSize,
      ...state.filters,
      ...(state.sort === null
        ? {}
        : { sort: `${state.sort.field}:${state.sort.direction}` }),
    }),
    [state],
  );

  const { data, isPending, isError } = useAssets(query);

  const columns: readonly Column<AssetSummary>[] = useMemo(
    () => [
      {
        key: 'name',
        header: t('asset.columns.name'),
        cell: (row) => row.name,
        sortField: 'name',
        card: 'primary',
      },
      {
        key: 'type',
        header: t('asset.columns.type'),
        cell: (row) => t(`asset.type.${row.type}`),
        sortField: 'type',
        card: 'secondary',
      },
      {
        key: 'status',
        header: t('asset.columns.status'),
        cell: (row) => <AssetStatusBadge status={row.status} />,
        sortField: 'status',
        card: 'secondary',
      },
      {
        key: 'capacity',
        header: t('asset.columns.capacity'),
        /* The pair PRD §9.5 requires, via the component that owns that rule —
           power alone is how a four-hour battery becomes a one-hour one. */
        cell: (row) => <Capacity mw={row.capacityMw} mwh={row.capacityMwh} />,
        sortField: 'capacityMw',
        numeric: true,
        card: 'secondary',
      },
      {
        key: 'owner',
        header: t('asset.columns.ownership'),
        /* The customer's name when there is one, else the ownership label:
           "Lactos do Minho" is more use than "Customer owned", and a
           Greenvolt site has no customer to name. */
        cell: (row) => row.customerName ?? t(`asset.ownership.${row.ownership}`),
        sortField: 'ownership',
        card: 'hidden',
      },
      {
        key: 'location',
        header: t('asset.columns.location'),
        cell: (row) =>
          row.location ?? row.country ?? <span className="text-gray-400">—</span>,
        card: 'hidden',
      },
      {
        key: 'suitability',
        header: t('asset.columns.suitability'),
        /* Suitability out of six, so a site that has been assessed is
           distinguishable from one nobody has looked at. */
        cell: (row) => (
          <span className="tabular-nums">
            {t('asset.columns.suitabilityRatio', {
              count: formatNumber(row.suitabilityCount),
            })}
          </span>
        ),
        numeric: true,
        card: 'hidden',
      },
      {
        key: 'verdict',
        header: t('asset.columns.verdict'),
        /**
         * The matchmaker's answer per row (F11).
         *
         * Three states, not two: ready, blocked, or neither — a development
         * site with no blockers is not ready and is not blocked, and collapsing
         * that into "not ready" would lose why.
         */
        cell: (row) =>
          row.isPilotReady ? (
            <Badge tone="ok">{t('asset.pilotReady')}</Badge>
          ) : row.blockerCount > 0 ? (
            <Badge tone="warn">
              {t('asset.columns.blockerCount', { count: row.blockerCount })}
            </Badge>
          ) : (
            <span className="text-gray-400">{t('asset.notReady')}</span>
          ),
        card: 'secondary',
      },
    ],
    [t],
  );

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <>
      <PageHead
        kicker={t('nav.group.relationships')}
        title={t('asset.title')}
        {...(data === undefined
          ? {}
          : { lead: t('asset.count', { count: data.meta.total }) })}
      />

      <div className="mb-s3 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <AssetFilters
            filters={state.filters}
            onFilterChange={setFilter}
            onClear={clearFilters}
          />
        </div>
        {/* Exports the on-screen filters and sort; hidden from Viewers (F18, §4). */}
        <ExportButton
          entity="assets"
          query={{
            ...state.filters,
            ...(state.sort === null
              ? {}
              : { sort: `${state.sort.field}:${state.sort.direction}` }),
          }}
        />
      </div>

      <DataTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        caption={t('asset.caption')}
        isLoading={isPending}
        sort={state.sort}
        onSortChange={toggleSort}
        emptyMessage={
          Object.keys(state.filters).length > 0
            ? t('asset.emptyFiltered')
            : t('asset.empty')
        }
        onRowSelect={(row) => {
          void navigate(assetPath(row.id));
        }}
      />

      {data === undefined ? null : (
        <Pagination
          page={data.meta.page}
          pageSize={data.meta.pageSize}
          total={data.meta.total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </>
  );
}
